import type { UnstructuredStrategy } from '@okrapdf/parser-unstructured';
import { OkraRuntimeError } from '../../errors';
import {
  createLiveViewServer,
  liveViewServerScopedEffect,
  liveViewServerErrorCause,
  isLoopbackHost,
  type LiveViewServer,
} from '../view/server';
import { createLiveViewSession, type LiveViewSession } from '../view/session';
import { Data, Effect, Exit, FileSystem, Path, Scope } from 'effect';
import { nodeServicesLayer } from '../node-platform.js';
import { launchProcessEffect, launcherErrorCause } from '../launcher-lifetime';
import { CommandSignalError, withCommandSignalsEffect } from '../command-lifetime';

export class ViewCommandError extends Data.TaggedError('ViewCommandError')<{
  readonly method: 'openLocalBrowser' | 'startLocalViewCommand' | 'runLocalViewCommand';
  readonly status?: number;
}> {}

const viewCommandErrorCauses = new WeakMap<ViewCommandError, unknown>();

function viewCommandFailure(method: ViewCommandError['method'], cause: unknown): ViewCommandError {
  const error = new ViewCommandError({
    method,
    ...(cause instanceof OkraRuntimeError ? { status: cause.status } : {}),
  });
  viewCommandErrorCauses.set(error, cause);
  return error;
}

export function viewCommandErrorCause(error: ViewCommandError): unknown {
  return viewCommandErrorCauses.get(error);
}

function viewBoundaryEffect<A>(
  method: ViewCommandError['method'],
  run: () => Promise<A>,
): Effect.Effect<A, ViewCommandError> {
  return Effect.tryPromise({
    try: run,
    catch: (cause) => viewCommandFailure(method, cause),
  });
}

function restoreViewCommandFailure<A>(
  program: Effect.Effect<A, ViewCommandError>,
): Effect.Effect<A, unknown> {
  return program.pipe(Effect.catch((error) => Effect.fail(viewCommandErrorCause(error))));
}

const STRATEGIES = new Set<UnstructuredStrategy>(['vlm', 'hi_res', 'fast', 'ocr_only', 'auto']);

export interface LocalViewCommandOptions {
  web?: boolean;
  provider?: string;
  strategy?: string;
  host?: string;
  port?: number;
  batchPages?: number;
  concurrency?: number;
  requestTimeout?: number;
  maxPages?: number;
  unstructuredUrl?: string;
}

export interface LocalViewCommandContext {
  json?: boolean;
  quiet?: boolean;
  output?: string;
}

export interface StartedLocalView {
  session: LiveViewSession;
  server: LiveViewServer;
  close(): Promise<void>;
}

interface LocalViewDependencies {
  env?: NodeJS.ProcessEnv;
  readPdf?: (path: string) => Promise<Uint8Array>;
  openBrowser?: (url: string) => Promise<void>;
  writeOut?: (value: string) => void;
  writeErr?: (value: string) => void;
  createSession?: typeof createLiveViewSession;
  createServer?: typeof createLiveViewServer;
}

function localViewIoError(cause: unknown): Error {
  return cause instanceof Error ? cause : new Error(String(cause));
}

function readLocalPdfEffect(
  pdf: string,
  readPdf: LocalViewDependencies['readPdf'],
): Effect.Effect<
  { readonly path: string; readonly title: string; readonly bytes: Uint8Array },
  Error
> {
  const program = Effect.gen(function* () {
    const fileSystem = yield* FileSystem.FileSystem;
    const paths = yield* Path.Path;
    const path = paths.resolve(process.cwd(), pdf);
    const readEffect = readPdf
      ? Effect.tryPromise({
          try: () => readPdf(path),
          catch: localViewIoError,
        })
      : fileSystem.readFile(path).pipe(Effect.mapError(localViewIoError));
    const bytes = yield* readEffect.pipe(
      Effect.mapError(
        (cause) => new Error(`Could not read PDF ${path}: ${cause.message}`, { cause }),
      ),
    );
    return { path, title: paths.basename(path, '.pdf'), bytes };
  }).pipe(Effect.provide(nodeServicesLayer));
  return program;
}

function writeLocalViewOutputEffect(path: string, content: string): Effect.Effect<void, Error> {
  const program = Effect.gen(function* () {
    const fileSystem = yield* FileSystem.FileSystem;
    yield* fileSystem.writeFileString(path, content).pipe(Effect.mapError(localViewIoError));
  }).pipe(Effect.provide(nodeServicesLayer));
  return program;
}

function positiveInteger(value: number | undefined, fallback: number, flag: string): number {
  const resolved = value ?? fallback;
  if (!Number.isSafeInteger(resolved) || resolved <= 0)
    throw new Error(`${flag} must be a positive integer.`);
  return resolved;
}

function validPort(value: number | undefined): number {
  const resolved = value ?? 0;
  if (!Number.isSafeInteger(resolved) || resolved < 0 || resolved > 65_535) {
    throw new Error('--port must be an integer between 0 and 65535.');
  }
  return resolved;
}

function providerEndpoint(raw: string | undefined): string {
  const value = raw?.trim() || 'https://api.unstructuredapp.io/general/v0/general';
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    throw new Error('--unstructured-url must be an absolute HTTP or HTTPS URL.');
  }
  if (
    !['http:', 'https:'].includes(parsed.protocol) ||
    parsed.username ||
    parsed.password ||
    parsed.search ||
    parsed.hash
  ) {
    throw new Error(
      '--unstructured-url must be an HTTP(S) URL without credentials, query parameters, or a fragment.',
    );
  }
  const hostname = parsed.hostname.toLowerCase().replace(/^\[|\]$/g, '');
  if (parsed.protocol === 'http:' && !['localhost', '127.0.0.1', '::1'].includes(hostname)) {
    throw new Error('--unstructured-url must use HTTPS unless it targets a loopback host.');
  }
  if (parsed.protocol === 'http:' && hostname === 'localhost') {
    // Do not let a modified hosts file redirect a plaintext request carrying
    // the provider key away from the local machine.
    parsed.hostname = '127.0.0.1';
  }
  return parsed.href;
}

function hasPdfMagic(bytes: Uint8Array): boolean {
  const prefix = new TextDecoder().decode(bytes.subarray(0, Math.min(bytes.length, 1024)));
  return prefix.includes('%PDF-');
}

function safeFailureMessage(error: unknown, apiKey: string): string {
  const raw = error instanceof Error ? error.message : String(error);
  return raw.split(apiKey).join('[REDACTED]').replace(/\s+/g, ' ').slice(0, 520);
}

function browserOpenCommand(url: string): string {
  if (process.platform === 'darwin') return `open ${url}`;
  if (process.platform === 'win32') return `start ${url}`;
  return `xdg-open ${url}`;
}

export function localBrowserEnvironment(
  source: NodeJS.ProcessEnv = process.env,
): NodeJS.ProcessEnv {
  const allowedEnvironment = new Set([
    'COMSPEC',
    'DBUS_SESSION_BUS_ADDRESS',
    'DESKTOP_SESSION',
    'DISPLAY',
    'HOME',
    'LANG',
    'LC_ALL',
    'PATH',
    'PATHEXT',
    'SHELL',
    'SYSTEMROOT',
    'TEMP',
    'TMP',
    'USERPROFILE',
    'WAYLAND_DISPLAY',
    'XAUTHORITY',
    'XDG_CURRENT_DESKTOP',
    'XDG_RUNTIME_DIR',
  ]);
  return Object.fromEntries(
    Object.entries(source).filter(
      ([name, value]) => value !== undefined && allowedEnvironment.has(name.toUpperCase()),
    ),
  ) as NodeJS.ProcessEnv;
}

export const openLocalBrowserEffect = Effect.fn('okrapdf/cli.openLocalBrowser')(function* (
  url: string,
) {
  const command =
    process.platform === 'darwin'
      ? { file: 'open', args: [url] }
      : process.platform === 'win32'
        ? { file: 'cmd', args: ['/c', 'start', '', url] }
        : { file: 'xdg-open', args: [url] };
  yield* launchProcessEffect(command.file, command.args, {
    stdio: 'ignore',
    windowsHide: true,
    env: localBrowserEnvironment(),
  }).pipe(
    Effect.mapError((error) => viewCommandFailure('openLocalBrowser', launcherErrorCause(error))),
  );
});

export function openLocalBrowser(url: string): Promise<void> {
  return Effect.runPromise(restoreViewCommandFailure(openLocalBrowserEffect(url)));
}

/** Native startup with resources registered before the next interruptible step. */
export const startLocalViewScopedEffect = Effect.fn('okrapdf/cli.startLocalViewScoped')(function* (
  pdf: string,
  options: LocalViewCommandOptions = {},
  context: LocalViewCommandContext = {},
  dependencies: LocalViewDependencies = {},
): Effect.fn.Return<StartedLocalView, ViewCommandError, Scope.Scope> {
  const syncBoundary = <A>(run: () => A) =>
    Effect.try({
      try: run,
      catch: (cause) => viewCommandFailure('startLocalViewCommand', cause),
    });
  const settings = yield* syncBoundary(() => {
    const env = dependencies.env ?? process.env;
    const writeOut = dependencies.writeOut ?? ((value: string) => process.stdout.write(value));
    const writeErr = dependencies.writeErr ?? ((value: string) => process.stderr.write(value));
    const provider = (options.provider ?? 'unstructured').trim().toLowerCase();
    if (provider !== 'unstructured') {
      throw new Error(
        `Unsupported --provider ${JSON.stringify(provider)}. This release supports unstructured.`,
      );
    }
    const strategy = (options.strategy ?? 'vlm').trim().toLowerCase() as UnstructuredStrategy;
    if (!STRATEGIES.has(strategy)) {
      throw new Error('--strategy must be one of: vlm, hi_res, fast, ocr_only, auto.');
    }

    const host = (options.host ?? '127.0.0.1').trim();
    if (!host) throw new Error('--host cannot be empty.');
    if (!isLoopbackHost(host)) {
      throw new Error(
        `Refusing non-loopback host ${host}. Local view supports localhost, 127.0.0.1, and ::1 only.`,
      );
    }

    const apiKey = (env.UNSTRUCTURED_API_KEY ?? '').trim();
    if (!apiKey) {
      throw new OkraRuntimeError(
        'INVALID_REQUEST',
        'UNSTRUCTURED_API_KEY is required. The key is read from the environment and never accepted as a CLI flag.',
        400,
        {
          error: 'missing_provider_key',
          hint: 'Export an Unstructured API key in the current shell, then retry.',
          retryable: false,
          next_actions: [
            {
              cmd: 'export UNSTRUCTURED_API_KEY="<your-key>"',
              why: 'Provide the Unstructured key without placing it in argv or shell history as a flag.',
            },
          ],
        },
      );
    }
    const endpoint = providerEndpoint(options.unstructuredUrl ?? env.UNSTRUCTURED_API_URL);
    const endpointHost = new URL(endpoint).host;

    return { env, writeOut, writeErr, strategy, host, apiKey, endpoint, endpointHost };
  });
  const { writeOut, writeErr, strategy, host, apiKey, endpoint, endpointHost } = settings;
  const {
    path: pdfPath,
    title,
    bytes: pdfBytes,
  } = yield* readLocalPdfEffect(pdf, dependencies.readPdf).pipe(
    Effect.mapError((cause) =>
      viewCommandFailure('startLocalViewCommand', localViewIoError(cause)),
    ),
  );
  yield* syncBoundary(() => {
    if (!hasPdfMagic(pdfBytes)) throw new Error(`The supplied file is not a PDF: ${pdfPath}`);
  });
  const session = yield* Effect.acquireRelease(
    syncBoundary(() =>
      (dependencies.createSession ?? createLiveViewSession)({
        title,
        fileName: 'document.pdf',
        pdfBytes,
        apiKey,
        endpoint,
        strategy,
        batchPages: positiveInteger(options.batchPages, 1, '--batch-pages'),
        concurrency: positiveInteger(options.concurrency, 4, '--concurrency'),
        requestTimeoutMs: positiveInteger(options.requestTimeout, 180, '--request-timeout') * 1_000,
        maxPages:
          options.maxPages === undefined
            ? undefined
            : positiveInteger(options.maxPages, options.maxPages, '--max-pages'),
        onFailure(error) {
          writeErr(`okra view: ${safeFailureMessage(error, apiKey)}\n`);
        },
      }),
    ),
    (session) => Effect.promise(() => session.close()),
  );

  const serverOptions = yield* syncBoundary(() => ({
    session,
    host,
    port: validPort(options.port),
  }));
  const server = yield* dependencies.createServer
    ? Effect.acquireRelease(
        viewBoundaryEffect('startLocalViewCommand', () =>
          dependencies.createServer!(serverOptions),
        ),
        (server) => Effect.promise(() => server.close()),
      )
    : liveViewServerScopedEffect(serverOptions).pipe(
        Effect.mapError((error) =>
          viewCommandFailure('startLocalViewCommand', liveViewServerErrorCause(error)),
        ),
      );
  const startupEnvelope = {
    ok: true,
    command: 'view',
    result: {
      object: 'local_live_html_server',
      provider: 'unstructured',
      strategy,
      url: server.address.url,
      shell_origin: server.address.shellOrigin,
      content_origin: server.address.contentOrigin,
      processing_host: endpointHost,
      disclosure:
        'The viewer is local; PDF bytes are sent directly to Unstructured. No Okra account or telemetry.',
    },
    cost: { usd: null },
    citations: [],
    next_actions: [
      { cmd: browserOpenCommand(server.address.url), why: 'Open the local live HTML viewer.' },
    ],
  };
  if (context.json || context.output) {
    const serialized = `${JSON.stringify(startupEnvelope)}\n`;
    if (context.output) {
      yield* writeLocalViewOutputEffect(context.output, serialized).pipe(
        Effect.mapError((cause) => viewCommandFailure('startLocalViewCommand', cause)),
      );
    } else {
      yield* syncBoundary(() => writeOut(serialized));
    }
  } else if (!context.quiet) {
    yield* syncBoundary(() =>
      writeErr(
        [
          '',
          'okra view is ready:',
          `  ${server.address.url}`,
          '',
          `Local UI/server: ${server.address.shellOrigin}`,
          `Document isolation: ${server.address.contentOrigin}`,
          `Processing: PDF bytes go directly to Unstructured at ${endpointHost}.`,
          'No Okra account, Okra API call, or Okra telemetry is used.',
          'Bound to loopback only.',
          'Ctrl-C to stop.',
          '',
        ].join('\n'),
      ),
    );
  }

  if (options.web) {
    yield* (
      dependencies.openBrowser
        ? viewBoundaryEffect('openLocalBrowser', () =>
            dependencies.openBrowser!(server.address.url),
          )
        : openLocalBrowserEffect(server.address.url)
    ).pipe(
      Effect.catch((error) =>
        syncBoundary(() => {
          const cause = viewCommandErrorCause(error);
          writeErr(
            `Could not open the browser automatically: ${cause instanceof Error ? cause.message : String(cause)}\n`,
          );
        }),
      ),
    );
  }
  // The session owns its native provider fiber; this scope owns session.close.
  yield* syncBoundary(() => {
    void session
      .start()
      .catch((error) => writeErr(`okra view: ${safeFailureMessage(error, apiKey)}\n`));
  });
  return { session, server, close: () => server.close() };
});

export const startLocalViewCommandEffect = Effect.fn('okrapdf/cli.startLocalViewCommand')(
  (
    pdf: string,
    options: LocalViewCommandOptions = {},
    context: LocalViewCommandContext = {},
    dependencies: LocalViewDependencies = {},
  ) =>
    Effect.uninterruptibleMask((restore) =>
      Effect.gen(function* () {
        const scope = yield* Scope.make();
        const started = yield* restore(
          startLocalViewScopedEffect(pdf, options, context, dependencies).pipe(
            Scope.provide(scope),
          ),
        ).pipe(Effect.onError((cause) => Scope.close(scope, Exit.failCause(cause))));
        let closing: Promise<void> | undefined;
        return {
          ...started,
          close: () => (closing ??= Effect.runPromise(Scope.close(scope, Exit.void))),
        };
      }),
    ),
);

export function startLocalViewCommand(
  pdf: string,
  options: LocalViewCommandOptions = {},
  context: LocalViewCommandContext = {},
  dependencies: LocalViewDependencies = {},
): Promise<StartedLocalView> {
  return Effect.runPromise(
    restoreViewCommandFailure(startLocalViewCommandEffect(pdf, options, context, dependencies)),
  );
}

/** Process signals are an imperative boundary; cleanup runs for signals and fiber interruption. */
export const waitForViewShutdownEffect = Effect.suspend(() => {
  let cleanup = () => {};
  return Effect.callback<void>((resume) => {
    const stop = () => resume(Effect.void);
    process.once('SIGINT', stop);
    process.once('SIGTERM', stop);
    cleanup = () => {
      process.off('SIGINT', stop);
      process.off('SIGTERM', stop);
    };
    return Effect.sync(cleanup);
  }).pipe(Effect.ensuring(Effect.sync(() => cleanup())));
});

export const runLocalViewCommandEffect = Effect.fn('okrapdf/cli.runLocalViewCommand')(
  (
    pdf: string,
    options: LocalViewCommandOptions = {},
    context: LocalViewCommandContext = {},
    dependencies: LocalViewDependencies = {},
  ): Effect.Effect<void, ViewCommandError> =>
    withCommandSignalsEffect(
      Effect.scoped(
        startLocalViewScopedEffect(pdf, options, context, dependencies).pipe(
          Effect.andThen(Effect.never),
        ),
      ),
    ).pipe(
      // View has historically treated either shutdown signal as a normal close.
      // Own the signals before startup while preserving that public exit contract.
      Effect.catch((error) =>
        error instanceof CommandSignalError ? Effect.void : Effect.fail(error),
      ),
    ),
);

export function runLocalViewCommand(
  pdf: string,
  options: LocalViewCommandOptions = {},
  context: LocalViewCommandContext = {},
): Promise<void> {
  return Effect.runPromise(
    restoreViewCommandFailure(runLocalViewCommandEffect(pdf, options, context)),
  );
}
